import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { DollarSign, ArrowLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Available Fiat Currencies (from Guardarian)
const FIAT_CURRENCIES = [
  { code: "EUR", name: "Euro", flag: "🇪🇺", region: "Europe" },
  { code: "USD", name: "US Dollar", flag: "🇺🇸", region: "Americas" },
  { code: "GBP", name: "British Pound Sterling", flag: "🇬🇧", region: "Europe" },
  { code: "TRY", name: "Turkish Lira", flag: "🇹🇷", region: "Europe" },
  { code: "DKK", name: "Danish Krone", flag: "🇩🇰", region: "Europe" },
  { code: "SEK", name: "Swedish Krona", flag: "🇸🇪", region: "Europe" },
  { code: "ZAR", name: "South African Rand", flag: "🇿🇦", region: "Africa" },
  { code: "CHF", name: "Swiss Franc", flag: "🇨🇭", region: "Europe" },
  { code: "CAD", name: "Canadian Dollar", flag: "🇨🇦", region: "Americas" },
  { code: "JPY", name: "Japanese Yen", flag: "🇯🇵", region: "Asia" },
  { code: "KRW", name: "South Korean Won", flag: "🇰🇷", region: "Asia" },
  { code: "AUD", name: "Australian Dollar", flag: "🇦🇺", region: "Pacific" },
  { code: "PLN", name: "Polish Zloty", flag: "🇵🇱", region: "Europe" },
  { code: "AED", name: "United Arab Emirates Dirham", flag: "🇦🇪", region: "Middle East" },
  { code: "NZD", name: "New Zealand Dollar", flag: "🇳🇿", region: "Pacific" },
  { code: "NGN", name: "Nigerian Naira", flag: "🇳🇬", region: "Africa" },
  { code: "COP", name: "Colombian Peso", flag: "🇨🇴", region: "Americas" },
  { code: "BHD", name: "Bahraini Dinar", flag: "🇧🇭", region: "Middle East" },
  { code: "BRL", name: "Brazilian Real", flag: "🇧🇷", region: "Americas" },
  { code: "TWD", name: "New Taiwan Dollar", flag: "🇹🇼", region: "Asia" },
  { code: "MXN", name: "Mexican Peso", flag: "🇲🇽", region: "Americas" },
  { code: "INR", name: "Indian Rupee", flag: "🇮🇳", region: "Asia" },
  { code: "BGN", name: "Bulgarian Lev", flag: "🇧🇬", region: "Europe" },
  { code: "DOP", name: "Dominican Peso", flag: "🇩🇴", region: "Americas" },
  { code: "THB", name: "Thai Baht", flag: "🇹🇭", region: "Asia" },
  { code: "RON", name: "Romanian Leu", flag: "🇷🇴", region: "Europe" },
  { code: "SGD", name: "Singapore Dollar", flag: "🇸🇬", region: "Asia" },
  { code: "NOK", name: "Norwegian Krone", flag: "🇳🇴", region: "Europe" },
  { code: "MYR", name: "Malaysian Ringgit", flag: "🇲🇾", region: "Asia" },
  { code: "CLP", name: "Chilean Peso", flag: "🇨🇱", region: "Americas" },
  { code: "BWP", name: "Botswana Pula", flag: "🇧🇼", region: "Africa" },
  { code: "PEN", name: "Peruvian Sol", flag: "🇵🇪", region: "Americas" },
  { code: "OMR", name: "Omani Rial", flag: "🇴🇲", region: "Middle East" },
  { code: "HKD", name: "Hong Kong Dollar", flag: "🇭🇰", region: "Asia" },
  { code: "KES", name: "Kenyan Shilling", flag: "🇰🇪", region: "Africa" },
  { code: "ARS", name: "Argentine Peso", flag: "🇦🇷", region: "Americas" },
  { code: "PHP", name: "Philippine Peso", flag: "🇵🇭", region: "Asia" },
  { code: "IDR", name: "Indonesian Rupiah", flag: "🇮🇩", region: "Asia" },
  { code: "BDT", name: "Bangladeshi Taka", flag: "🇧🇩", region: "Asia" },
  { code: "GHS", name: "Ghanaian Cedi", flag: "🇬🇭", region: "Africa" },
  { code: "VND", name: "Vietnamese Dong", flag: "🇻🇳", region: "Asia" },
  { code: "CZK", name: "Czech Koruna", flag: "🇨🇿", region: "Europe" },
  { code: "ILS", name: "Israeli Shekel", flag: "🇮🇱", region: "Middle East" },
  { code: "HUF", name: "Hungarian Forint", flag: "🇭🇺", region: "Europe" }
];

export default function FiatCurrenciesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');

  const regions = useMemo(() => {
    return [...new Set(FIAT_CURRENCIES.map(c => c.region))].sort();
  }, []);

  const filteredCurrencies = useMemo(() => {
    const search = searchTerm.toLowerCase();
    return FIAT_CURRENCIES.filter(currency => 
      (selectedRegion === 'all' || currency.region === selectedRegion) &&
      (currency.code.toLowerCase().includes(search) || 
       currency.name.toLowerCase().includes(search))
    );
  }, [searchTerm, selectedRegion]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
              <DollarSign className="w-8 h-8 text-purple-600" />
              Fiat Currencies
            </h1>
            <p className="text-gray-600">
              Send and receive in <span className="font-semibold text-purple-600">{FIAT_CURRENCIES.length} currencies</span> worldwide
            </p>
          </div>

          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search currencies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={selectedRegion === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedRegion('all')}
                  >
                    All
                  </Button>
                  {regions.map((region) => (
                    <Button
                      key={region}
                      variant={selectedRegion === region ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedRegion(region)}
                    >
                      {region}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-600" />
                Available Currencies
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({filteredCurrencies.length} currencies)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredCurrencies.map((currency) => (
                  <div
                    key={currency.code}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-gray-50 to-purple-50 border border-purple-100 hover:shadow-md hover:border-purple-300 transition-all"
                  >
                    <span className="text-2xl">{currency.flag}</span>
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-gray-800">
                        {currency.code}
                      </span>
                      <span className="text-xs text-gray-500 truncate max-w-[100px]">
                        {currency.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              More currencies are being added regularly.
            </p>
            <p className="mt-1">
              Powered by <a href="https://guardarian.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">Guardarian</a> payment infrastructure
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
